import React from 'react';

import { Tooltip } from 'antd';

import { option, pipe } from '@code-expert/prelude';
import type { DateTimeFormat } from '/imports/ui/foundation/DateTime/format';
import { formatDateTime } from '/imports/ui/foundation/DateTime/format';
import { styled } from '/imports/ui/foundation/Theme';
import type { DateComponentProps } from './shared';

const StyledDateWrap = styled('span', ({ tokens }) => ({
  whiteSpace: 'nowrap',
  variants: {
    wrap: {
      true: {
        whiteSpace: 'normal',
      },
    },
    tabular: {
      true: {
        fontVariant: 'tabular-nums',
        fontFeatureSettings: "'tnum'",
        letterSpacing: 'normal',
      },
    },
    empty: {
      true: {
        color: tokens.colorTextTertiary,
      },
    },
  },
}));

export interface DateTimeProps extends DateComponentProps {
  format: DateTimeFormat;
  date: Date;
  showTooltip?: boolean;
}

export const DateTime = ({
  format,
  date,
  allowWrap = false,
  showTooltip = false,
  hideInTests = false,
}: DateTimeProps): React.JSX.Element => {
  const element = (
    <StyledDateWrap
      wrap={allowWrap}
      tabular={format === 'tabular'}
      data-test="date-time"
      data-hide-in-tests={hideInTests || undefined}
    >
      <span>{formatDateTime(format)(date)}</span>
    </StyledDateWrap>
  );
  return showTooltip ? (
    <Tooltip title={formatDateTime('timestamp')(date)}>{element}</Tooltip>
  ) : (
    element
  );
};

export interface DateTimeOptionalProps extends DateComponentProps {
  format: DateTimeFormat;
  date?: Date;
  fallback?: React.ReactNode;
  showTooltip?: boolean;
}

export const DateTimeOptional = ({
  format,
  date,
  fallback,
  ...rest
}: DateTimeOptionalProps): React.JSX.Element =>
  pipe(
    option.fromNullable(date),
    option.fold(
      () =>
        fallback === undefined ? (
          <StyledDateWrap empty>(not specified)</StyledDateWrap>
        ) : (
          <StyledDateWrap>{fallback}</StyledDateWrap>
        ),
      (d) => <DateTime {...rest} format={format} date={d} />,
    ),
  );
